import {
  type PropsWithChildren,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";

import clsx from "clsx";
import { isNil, keyBy } from "lodash";
import omitDeep from "omit-deep-lodash";

import { GenericExternalLink } from "@components/generic-external-link";
import { useUser } from "@components/user-context";
import {
  FETCH_BENCHMARK_SETTINGS,
  SET_BENCHMARK_TARGET,
} from "@gql/benchmarks";
import { useNorthbeamMutation } from "@hooks/http";
import { prettyLogEvent } from "@internal/analytics";
import {
  type BenchmarkAttributionModelEnum,
  type BenchmarkAttributionWindowEnum,
  type BenchmarkPlatformEnum,
  type BenchmarkTypeEnum,
  type PlatformCACRoasBenchmarksInput,
  type UpsertBenchmarkTargets,
  type UpsertBenchmarkTargetsVariables,
} from "@north-beam/nb-client-side";
import { Pages } from "@north-beam/nb-client-side/constants";
import { IconButton } from "@shared/buttons";
import { Modal } from "@shared/modal";
import { notifySuccess } from "@shared/notify";

import {
  type BenchmarkModalNavigationRoute,
  useBackRouteAtom,
  useCurrentBenchmarkRouteAtom,
  useCustomBenchmarksByPlatform,
  useHasUnsavedChangesAtom,
  useNBCalculatedBenchmarksByPlatform,
} from "../atoms";
import {
  useBenchmarkAttributionWindow,
  useBenchmarks,
  useGetBenchmarkPlatformsToUse,
} from "../hooks";

import {
  BenchmarkChooseAndSet,
  type ChooseAndSetValidationTargets,
} from "./benchmark-choose-and-set-modal";
import { BENCHMARK_HELP_LINK } from "./benchmark-help";
import { BenchmarkReview } from "./benchmark-review-modal";
import { CancelConfirmationModal } from "./cancel-confirmation-modal";
import {
  type BenchmarksByPlatform,
  CustomBenchmarks,
  type PlatformBenchmarks,
} from "./custom-benchmarks-modal";

const BenchmarkStep = ({
  step,
  active,
  checked,
}: {
  step: BenchmarkModalNavigationRoute;
  active: boolean;
  checked: boolean;
}) => {
  return (
    <div
      className={clsx(
        "w-full pl-5 lg:pl-8 py-3 flex items-center text-sm lg:text-base",
        active ? "bg-cosmic-3" : undefined,
      )}
    >
      {checked && (
        <i className="fa-regular fa-sharp fa-circle-check pr-2 text-primary text-lg" />
      )}
      {!checked && <i className="fa-regular fa-sharp fa-circle pr-2 text-lg" />}
      {step === "choose-and-set" ? "Choose and set" : "Review"}
    </div>
  );
};

const BenchmarkModalSkeleton = ({
  children,
  closeModal,
  hasContentPadding,
}: PropsWithChildren<{
  closeModal: () => void;
  hasContentPadding?: boolean;
}>) => {
  const [prevRoute] = useBackRouteAtom();
  const [potentialCurrentStep] = useCurrentBenchmarkRouteAtom();
  const currentStep =
    potentialCurrentStep === "help-me" ? prevRoute : potentialCurrentStep;
  const currentStepIsChooseAndSet = currentStep === "choose-and-set";
  const { user } = useUser();

  return (
    <Modal
      size="3xl"
      isOpen={true}
      onClose={closeModal}
      hideCloseButton
      bodyClassName="px-0 py-0"
    >
      <div className="xl:w-[70rem] lg:w-[60rem] w-[95vw] flex bg-cosmic-4">
        <IconButton onClick={closeModal} className="absolute top-8 right-8 z-1">
          <i className="fa-regular fa-sharp fa-xmark text-2xl" />
        </IconButton>
        <div className="w-[14rem] lg:w-[17rem] pt-10 h-full">
          <div className="text-sm lg:text-base font-semibold pl-5 lg:pl-8">
            Setup a benchmark
          </div>
          <div
            className={clsx("pt-4", {
              "opacity-25": potentialCurrentStep === "help-me",
            })}
          >
            <BenchmarkStep
              active={currentStepIsChooseAndSet}
              step="choose-and-set"
              checked
            />
            <BenchmarkStep
              active={!currentStepIsChooseAndSet}
              /* choose one of "review-with-nb-benchmarks" or "review-with-custom-benchmarks"
                 we treat them the same here */
              step="review-with-nb-benchmarks"
              checked={currentStep !== "choose-and-set"}
            />
          </div>
          <GenericExternalLink
            href={BENCHMARK_HELP_LINK}
            className="absolute bottom-7 left-5 lg:left-8 cursor-pointer"
            onClick={() => {
              if (potentialCurrentStep !== "help-me") {
                prettyLogEvent.navigateToPage({
                  user,
                  page: Pages.SALES,
                  domain: "Benchmarking - Help Modal",
                });
              }
            }}
          >
            <i className="fa-regular fa-sharp fa-circle-question text-[1.5rem] text-[#555860]" />
          </GenericExternalLink>
        </div>
        <div
          className={clsx(
            "py-8 px-5 lg:px-8 w-full 2xlh:h-[50rem] xl:h-[40rem] lg:h-[35rem]",
            "md:h-[20rem] sm:h-[15rem] bg-white",
          )}
        >
          {children}
        </div>
      </div>
    </Modal>
  );
};

type BenchmarkModalProps = {
  closeModal: () => void;
  attributionModel: BenchmarkAttributionModelEnum;
};

// This flow is specifically where you go straight to the Custom Benchmarks Modal
export const CustomBenchmarksFlowModal = ({
  closeModal,
  initialModalNavigation,
  setInitialModalNavigation,
  attributionModel,
}: BenchmarkModalProps & {
  initialModalNavigation: BenchmarkModalNavigationRoute;
  setInitialModalNavigation: (
    initalModalNav: BenchmarkModalNavigationRoute | null,
  ) => void;
}) => {
  const [hasUnsavedChanges] = useHasUnsavedChangesAtom();
  const { user } = useUser();

  const platformsToUse = useGetBenchmarkPlatformsToUse();
  const [currentRoute, navigateTo] = useCurrentBenchmarkRouteAtom();
  const [, setCustomBenchmarksByPlatform] = useCustomBenchmarksByPlatform();
  useEffect(() => {
    if (initialModalNavigation) {
      navigateTo(initialModalNavigation);
    }
  }, [initialModalNavigation, navigateTo]);
  const memoizedAttributionModels = useMemo(() => {
    return [attributionModel];
  }, [attributionModel]);

  const [currentAttributionWindowOption] = useBenchmarkAttributionWindow();

  const {
    benchmarkSettings,
    benchmarkSettingsLoading,
    platformBenchmarksLoading,
    orderedData,
    customBenchmarks,
  } = useBenchmarks(
    attributionModel,
    currentAttributionWindowOption,
    platformsToUse,
    memoizedAttributionModels,
  );

  const benchmarksByPlatform = useMemo(() => {
    return orderedData?.reduce<BenchmarksByPlatform>((acc, curr) => {
      if (!acc[curr.platform]) {
        acc[curr.platform] = [];
      }
      acc[curr.platform].push(curr as PlatformBenchmarks);
      return acc;
    }, {} as BenchmarksByPlatform);
  }, [orderedData]);

  useEffect(() => {
    if (!customBenchmarks) {
      setCustomBenchmarksByPlatform(null);
      return;
    }

    setCustomBenchmarksByPlatform(
      omitDeep(
        customBenchmarks.reduce<BenchmarksByPlatform>((acc, curr) => {
          if (!acc[curr.platform]) {
            acc[curr.platform] = [];
          }
          acc[curr.platform].push(curr as PlatformBenchmarks);
          return acc;
        }, {} as BenchmarksByPlatform),
        "__typename",
      ) as BenchmarksByPlatform,
    );
  }, [customBenchmarks, setCustomBenchmarksByPlatform]);

  const [submitBenchmarkTarget, { loading: loadingSubmission }] =
    useNorthbeamMutation<
      UpsertBenchmarkTargets,
      UpsertBenchmarkTargetsVariables
    >(SET_BENCHMARK_TARGET, {
      refetchQueries: [
        {
          query: FETCH_BENCHMARK_SETTINGS,
          variables: {
            attributionWindow: currentAttributionWindowOption,
            attributionModel,
          },
        },
      ],
    });
  const loading =
    benchmarkSettingsLoading || platformBenchmarksLoading || loadingSubmission;

  // there should only ever be one benchmarkType
  const benchmarkType = useMemo(() => {
    return benchmarkSettings?.[0].benchmark;
  }, [benchmarkSettings]);

  const benchmarkTargetsByPlatform = useMemo(() => {
    const targetsByPlatform = {} as ChooseAndSetValidationTargets;
    benchmarkSettings?.forEach((setting) => {
      targetsByPlatform[setting.platform] = setting.target;
    });
    return targetsByPlatform;
  }, [benchmarkSettings]);

  const onSubmit = useCallback(
    async (submittedCustomBenchmarksByPlatform?: BenchmarksByPlatform) => {
      if (!benchmarkSettings) {
        return;
      }
      const benchmarkSettingsByPlatform = keyBy(benchmarkSettings, "platform");

      const benchmarkTargetsInput: UpsertBenchmarkTargetsVariables["benchmarkTargets"] =
        [];
      if (submittedCustomBenchmarksByPlatform) {
        Object.entries(submittedCustomBenchmarksByPlatform).forEach(
          ([platform, customBenchmarks]) => {
            const { attributionWindow, benchmark, target } =
              benchmarkSettingsByPlatform[platform];
            return benchmarkTargetsInput.push({
              attributionModel,
              attributionWindow,
              benchmarkType: benchmark,
              customBenchmarks:
                customBenchmarks as PlatformCACRoasBenchmarksInput[],
              platform: platform as BenchmarkPlatformEnum,
              target,
            });
          },
        );
      }

      await submitBenchmarkTarget({
        variables: {
          benchmarkTargets: benchmarkTargetsInput,
        },
      });
      notifySuccess({
        message:
          "Congratulations!  You have successfully set 1 day buying benchmarks.",
      });
      prettyLogEvent.onSubmit({
        page: Pages.SALES,
        domain: "Benchmarking - Modal",
        user,
        additionalActionInfo: submittedCustomBenchmarksByPlatform
          ? "custom-benchmarks"
          : "nb-calculated-submission",
        data: {
          benchmarkTargetsInput,
        },
      });
      closeModal();
    },
    [
      attributionModel,
      benchmarkSettings,
      user,
      closeModal,
      submitBenchmarkTarget,
    ],
  );
  const [cancelConfirmationModalOpen, setCancelConfirmationModal] =
    useState(false);
  const [startOver, setDoStartOver] = useState(false);

  return (
    <BenchmarkModalSkeleton
      hasContentPadding={false}
      closeModal={() => {
        if (hasUnsavedChanges) {
          prettyLogEvent.onClick({
            user,
            page: Pages.SALES,
            domain: "Benchmarking - Modal",
            additionalActionInfo: "close-attempt",
          });
          return setCancelConfirmationModal(true);
        }
        setInitialModalNavigation(null);
        prettyLogEvent.onExit({
          user,
          page: Pages.SALES,
          domain: "Benchmarking - Modal",
        });
        return closeModal();
      }}
    >
      <CancelConfirmationModal
        open={cancelConfirmationModalOpen}
        goBack={() => setCancelConfirmationModal(false)}
        onConfirmCancel={() => {
          if (startOver) {
            navigateTo("choose-and-set");
            setInitialModalNavigation(null);
            setCustomBenchmarksByPlatform(null);
            prettyLogEvent.onClick({
              user,
              page: Pages.SALES,
              domain: "Benchmarking - Modal",
              additionalActionInfo: "start-over",
            });
          } else {
            prettyLogEvent.onExit({
              user,
              page: Pages.SALES,
              domain: "Benchmarking - Modal",
            });
            closeModal();
          }
        }}
      />
      {currentRoute === "review-with-custom-benchmarks" &&
        benchmarksByPlatform &&
        benchmarkType && (
          <CustomBenchmarks
            benchmarkTargetsByPlatform={benchmarkTargetsByPlatform}
            benchmarkType={benchmarkType}
            nbBenchmarksByPlatform={benchmarksByPlatform}
            dataIsNullable={false}
            loading={loading}
            onSubmit={onSubmit}
            attributionModel={attributionModel}
            standalone={{
              startOver: () => {
                if (hasUnsavedChanges) {
                  setCancelConfirmationModal(true);
                  setDoStartOver(true);
                } else {
                  navigateTo("choose-and-set");
                  setInitialModalNavigation(null);
                  setCustomBenchmarksByPlatform(null);
                  prettyLogEvent.onClick({
                    user,
                    page: Pages.SALES,
                    domain: "Benchmarking - Modal",
                    additionalActionInfo: "start-over",
                  });
                }
              },
            }}
          />
        )}
    </BenchmarkModalSkeleton>
  );
};

export const BenchmarkModal = ({
  closeModal,
  attributionModel,
}: BenchmarkModalProps) => {
  const { user } = useUser();

  const [benchmarkType, setBenchmarkType] = useState<BenchmarkTypeEnum | null>(
    null,
  );
  const [benchmarkAttributionWindow, setBenchmarkAttributionWindow] =
    useState<BenchmarkAttributionWindowEnum | null>(null);
  const [benchmarkTargets, setBenchmarkTargets] =
    useState<ChooseAndSetValidationTargets>();

  const [cancelConfirmationModalOpen, setCancelConfirmationModal] =
    useState(false);

  const [hasUnsavedChanges, setHasUnsavedChanges] = useHasUnsavedChangesAtom();
  const allSelected =
    benchmarkType && benchmarkAttributionWindow && !isNil(benchmarkTargets);

  const [currentRoute, navigateTo] = useCurrentBenchmarkRouteAtom();

  const [nbCalculatedBenchmarksByPlatform] =
    useNBCalculatedBenchmarksByPlatform();

  const [submitBenchmarkTarget, { loading: loadingSubmission }] =
    useNorthbeamMutation<
      UpsertBenchmarkTargets,
      UpsertBenchmarkTargetsVariables
    >(SET_BENCHMARK_TARGET, {
      refetchQueries: [
        {
          query: FETCH_BENCHMARK_SETTINGS,
          variables: {
            attributionWindow: benchmarkAttributionWindow,
            attributionModel,
          },
        },
      ],
    });

  const onSubmit = useCallback(
    async (submittedCustomBenchmarksByPlatform?: BenchmarksByPlatform) => {
      if (!allSelected) {
        return;
      }

      await submitBenchmarkTarget({
        variables: {
          benchmarkTargets: Object.entries(benchmarkTargets).map(
            ([platform, target]) => {
              const customBenchmarks = submittedCustomBenchmarksByPlatform?.[
                platform as BenchmarkPlatformEnum
              ] as PlatformCACRoasBenchmarksInput[];
              return {
                attributionModel,
                attributionWindow: benchmarkAttributionWindow,
                benchmarkType,
                platform: platform as BenchmarkPlatformEnum,
                target,
                customBenchmarks,
              };
            },
          ),
        },
      });
      prettyLogEvent.onSubmit({
        user,
        page: Pages.SALES,
        domain: "Benchmarking - Modal",
        additionalActionInfo: submittedCustomBenchmarksByPlatform
          ? "custom-benchmarks"
          : "nb-calculated-submission",
        data: {
          benchmarkTargets,
          benchmark: benchmarkType,
          attributionModel,
          attributionWindow: benchmarkAttributionWindow,
        },
      });
      closeModal();
      notifySuccess({
        message:
          "Congratulations!  You have successfully set 1 day buying benchmarks.",
      });
    },
    [
      user,
      allSelected,
      attributionModel,
      benchmarkAttributionWindow,
      benchmarkTargets,
      benchmarkType,
      closeModal,
      submitBenchmarkTarget,
    ],
  );

  return (
    <>
      <BenchmarkModalSkeleton
        closeModal={() => {
          if (hasUnsavedChanges) {
            return setCancelConfirmationModal(true);
          }
          prettyLogEvent.onExit({
            user,
            page: Pages.SALES,
            domain: "Benchmarking - Modal",
          });
          return closeModal();
        }}
        hasContentPadding={false}
      >
        {currentRoute === "choose-and-set" && (
          <BenchmarkChooseAndSet
            onNext={({
              targetsByPlatform,
              benchmarkType,
              attributionWindow,
            }) => {
              setBenchmarkTargets(targetsByPlatform);
              setBenchmarkAttributionWindow(attributionWindow);
              setBenchmarkType(benchmarkType);
              navigateTo("review-with-nb-benchmarks");
            }}
            setHasUnsavedChanges={setHasUnsavedChanges}
            currentBenchmarkType={benchmarkType}
            currentBenchmarkAttributionWindow={benchmarkAttributionWindow}
            currentBenchmarkTargets={benchmarkTargets}
            attributionModel={attributionModel}
          />
        )}
        {currentRoute === "review-with-nb-benchmarks" && allSelected && (
          <BenchmarkReview
            benchmarkType={benchmarkType}
            attributionWindow={benchmarkAttributionWindow}
            benchmarkTargetsByPlatform={benchmarkTargets}
            closeModal={closeModal}
            loadingSubmission={loadingSubmission}
            onSubmit={onSubmit}
            attributionModel={attributionModel}
          />
        )}
        {currentRoute === "review-with-custom-benchmarks" &&
          allSelected &&
          nbCalculatedBenchmarksByPlatform && (
            <CustomBenchmarks
              nbBenchmarksByPlatform={nbCalculatedBenchmarksByPlatform}
              dataIsNullable={false}
              loading={loadingSubmission}
              onSubmit={onSubmit}
              attributionModel={attributionModel}
              benchmarkType={benchmarkType}
              benchmarkTargetsByPlatform={benchmarkTargets}
            />
          )}
      </BenchmarkModalSkeleton>
      <CancelConfirmationModal
        open={cancelConfirmationModalOpen}
        goBack={() => setCancelConfirmationModal(false)}
        onConfirmCancel={() => {
          setCancelConfirmationModal(false);
          closeModal();
          prettyLogEvent.onExit({
            user,
            page: Pages.SALES,
            domain: "Benchmarking - Modal",
          });
        }}
      />
    </>
  );
};
